import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

// Types
export interface Business {
  id: string;
  name: string;
  slug: string | null;
  cnpj: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  logo_url: string | null;
  settings: unknown;
  segment: string | null;
  cash_register_whatsapp_report: boolean | null;
  cash_register_report_phone: string | null;
  created_at: string;
  updated_at: string;
}

export interface Profile {
  id: string;
  business_id: string | null;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  created_at: string;
  updated_at: string;
}

export interface Subscription {
  id: string;
  business_id: string;
  plan_id: string;
  status: "active" | "cancelled" | "past_due" | "trialing";
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
  subscription_plans?: SubscriptionPlan;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  monthly_price: number;
  yearly_price: number;
  max_users: number;
  max_customers: number | null;
  max_emails_month: number | null;
  max_whatsapp_month: number | null;
  features: string[];
  is_active: boolean;
}

// Fetch current user's profile
export const useProfile = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data as Profile | null;
    },
    enabled: !!user,
  });
};

// Fetch current user's business (with impersonation support)
export const useBusiness = () => {
  const { data: profile } = useProfile();
  
  // Check for impersonation
  const impersonateBusinessId = typeof window !== 'undefined' 
    ? sessionStorage.getItem('impersonate_business_id') 
    : null;

  const businessId = impersonateBusinessId || profile?.business_id;

  return useQuery({
    queryKey: ["business", businessId, !!impersonateBusinessId],
    queryFn: async () => {
      if (!businessId) return null;

      const { data, error } = await supabase
        .from("businesses")
        .select("*")
        .eq("id", businessId)
        .maybeSingle();

      if (error) throw error;
      return data as Business | null;
    },
    enabled: !!businessId,
  });
};

// Update business
export const useUpdateBusiness = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();

  return useMutation({
    mutationFn: async (updates: Partial<Omit<Business, "settings">> & { settings?: Record<string, unknown> }) => {
      if (!profile?.business_id) throw new Error("No business found");

      const { data, error } = await supabase
        .from("businesses")
        .update(updates as any)
        .eq("id", profile.business_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business"] });
      toast({
        title: "Sucesso",
        description: "Dados atualizados com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Não foi possível atualizar os dados.",
        variant: "destructive",
      });
    },
  });
};

// Fetch subscription with plan details
export const useSubscription = () => {
  const { data: profile } = useProfile();

  return useQuery({
    queryKey: ["subscription", profile?.business_id],
    queryFn: async () => {
      if (!profile?.business_id) return null;

      const { data, error } = await supabase
        .from("subscriptions")
        .select(`
          *,
          subscription_plans (*)
        `)
        .eq("business_id", profile.business_id)
        .maybeSingle();

      if (error) throw error;
      return data as (Subscription & { subscription_plans: SubscriptionPlan }) | null;
    },
    enabled: !!profile?.business_id,
  });
};

// Fetch all subscription plans
export const useSubscriptionPlans = () => {
  return useQuery({
    queryKey: ["subscription_plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("is_active", true)
        .order("monthly_price", { ascending: true });

      if (error) throw error;
      return data as SubscriptionPlan[];
    },
  });
};
